import { useState } from 'react';
import { useTheme } from '../hooks/useTheme';
import { useVeloraStore } from '../store/useVeloraStore';
import { QRScanner } from '../components/QRScanner';

// ── Section wrapper ────────────────────────────────────────────────────────
function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="fade-up">
      <p className="text-xs tracking-widest uppercase mb-2"
        style={{ color: 'rgba(238,237,254,0.30)', fontFamily: 'Syne, sans-serif', paddingLeft: 4 }}>
        {title}
      </p>
      <div className="glass-card rounded-2xl overflow-hidden mb-4">
        {children}
      </div>
    </div>
  );
}

// ── Row ────────────────────────────────────────────────────────────────────
function Row({
  icon, label, sub, action, border = true,
}: {
  icon: string; label: string; sub?: string;
  action: React.ReactNode; border?: boolean;
}) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12,
      padding: '13px 16px',
      borderBottom: border ? '1px solid rgba(255,255,255,0.06)' : 'none',
      position: 'relative', zIndex: 2,
    }}>
      <div style={{
        width: 34, height: 34, borderRadius: 10, flexShrink: 0,
        background: 'rgba(155,148,232,0.12)',
        border: '1px solid rgba(155,148,232,0.18)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16,
      }}>{icon}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ color: '#EEEDFE', fontSize: 14, fontWeight: 500, fontFamily: 'Plus Jakarta Sans, sans-serif', margin: 0 }}>{label}</p>
        {sub && <p style={{ color: 'rgba(238,237,254,0.40)', fontSize: 11, margin: '1px 0 0', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>{sub}</p>}
      </div>
      <div style={{ flexShrink: 0 }}>{action}</div>
    </div>
  );
}

// ── Toggle ─────────────────────────────────────────────────────────────────
function Toggle({ value, onChange }: { value: boolean; onChange: () => void }) {
  return (
    <button onClick={onChange} style={{
      width: 44, height: 24, borderRadius: 12,
      background: value ? 'rgba(127,119,221,0.70)' : 'rgba(255,255,255,0.15)',
      border: `1px solid ${value ? 'rgba(127,119,221,0.85)' : 'rgba(255,255,255,0.15)'}`,
      position: 'relative', cursor: 'pointer',
      boxShadow: value ? '0 0 12px rgba(127,119,221,0.40)' : 'none',
      transition: 'all 0.2s',
    }}>
      <span style={{
        position: 'absolute', top: 2, height: 18, width: 18, borderRadius: '50%',
        background: 'rgba(255,255,255,0.90)',
        left: value ? '22px' : '2px',
        boxShadow: '0 1px 4px rgba(0,0,0,0.30)',
        transition: 'left 0.2s',
      }} />
    </button>
  );
}

export default function Settings() {
  const theme = useTheme();
  const {
    desktopHost, setDesktopHost, wsEnabled, setWsEnabled,
    theme: themeName, setTheme, lang, setLang,
    userName, setUserName,
    singingStyles,
    trainingGoal,
    setOnboarded,
  } = useVeloraStore();
  const [hostInput, setHostInput] = useState(desktopHost);
  const [scanning, setScanning] = useState(false);
  const [editName, setEditName] = useState(false);
  const [nameInput, setNameInput] = useState(userName);

  function handleQRResult(text: string) {
    setScanning(false);
    try {
      const data = JSON.parse(text) as { ip?: string; port?: number };
      if (data.ip) {
        const host = data.port ? `${data.ip}:${data.port}` : data.ip;
        setHostInput(host); setDesktopHost(host);
      }
    } catch { /* plain IP */ }
  }

  const THEMES_LIST = [
    { id: 'dark' as const, label: 'Dark', icon: '🌑', bg: '#050210', accent: '#534AB7' },
    { id: 'light' as const, label: 'Light', icon: '☀️', bg: '#ece9ff', accent: '#534AB7' },
    { id: 'cosmos' as const, label: 'Cosmos', icon: '🌌', bg: '#020108', accent: '#7F77DD' },
  ];

  const STYLE_LABELS: Record<string, string> = {
    pop: 'Pop', rock: 'Rock', classical: 'Classical', jazz: 'Jazz',
    rnb: 'R&B', musical: 'Musical', folk: 'Folk', any: 'Everything',
  };
  const GOAL_LABELS: Record<string, string> = {
    learn_songs: 'Learn Songs', technique: 'Improve Technique',
    daily_warmup: 'Daily Warmup', performance: 'Performance Prep',
  };

  return (
    <div className="flex flex-col min-h-screen pb-24" style={{ background: 'transparent' }}>

      {/* Header */}
      <div className="safe-top px-5 pt-4 pb-5 fade-up">
        <p className="text-xs tracking-widest uppercase font-medium mb-1"
          style={{ color: theme.dim, fontFamily: 'Syne, sans-serif' }}>Configuration</p>
        <h1 className="text-2xl font-bold" style={{ color: theme.text, fontFamily: 'Syne, sans-serif' }}>
          SETTINGS
        </h1>
      </div>

      <div className="px-4">

        {/* ── PROFILE ── */}
        <Section title="Profile">
          <Row icon="👤" label="Name" sub={userName || 'Not set'}
            action={
              editName ? (
                <div style={{ display: 'flex', gap: 6 }}>
                  <input value={nameInput} onChange={e => setNameInput(e.target.value)} autoFocus
                    style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, padding: '4px 8px', color: '#EEEDFE', fontSize: 13, width: 100, outline: 'none' }} />
                  <button onClick={() => { setUserName(nameInput); setEditName(false); }}
                    style={{ background: 'rgba(83,74,183,0.30)', border: '1px solid rgba(127,119,221,0.40)', borderRadius: 8, color: '#EEEDFE', fontSize: 12, padding: '4px 8px', cursor: 'pointer' }}>Save</button>
                </div>
              ) : (
                <button onClick={() => { setNameInput(userName); setEditName(true); }}
                  style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 8, color: 'rgba(238,237,254,0.50)', fontSize: 12, padding: '5px 10px', cursor: 'pointer' }}>Edit</button>
              )
            }
          />
          <Row icon="🎭" label="Voice Type" sub="Set via Range test" border={false}
            action={
              <span style={{ color: theme.accent, fontSize: 13, fontFamily: 'Syne, sans-serif' }}>
                {useVeloraStore.getState().voiceType || '—'}
              </span>
            }
          />
        </Section>

        {/* ── TRAINING PREFS ── */}
        <Section title="Training Preferences">
          <Row icon="🎼" label="Singing Styles"
            sub={singingStyles.length ? singingStyles.map(s => STYLE_LABELS[s] || s).join(', ') : 'Not set'}
            action={
              <button onClick={() => setOnboarded(false)}
                style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 8, color: 'rgba(238,237,254,0.50)', fontSize: 12, padding: '5px 10px', cursor: 'pointer' }}>Change</button>
            }
          />
          <Row icon="🎯" label="Training Goal" sub={GOAL_LABELS[trainingGoal] || 'Not set'} border={false}
            action={
              <button onClick={() => setOnboarded(false)}
                style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 8, color: 'rgba(238,237,254,0.50)', fontSize: 12, padding: '5px 10px', cursor: 'pointer' }}>Change</button>
            }
          />
        </Section>

        {/* ── APPEARANCE ── */}
        <Section title="Appearance">
          <div style={{ padding: '12px 16px', position: 'relative', zIndex: 2 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
              {THEMES_LIST.map(t => (
                <button key={t.id} onClick={() => setTheme(t.id)} style={{
                  padding: '10px 6px', borderRadius: 14,
                  background: themeName === t.id ? 'rgba(83,74,183,0.22)' : 'rgba(255,255,255,0.04)',
                  backdropFilter: 'blur(12px)',
                  border: `1px solid ${themeName === t.id ? 'rgba(127,119,221,0.45)' : 'rgba(255,255,255,0.08)'}`,
                  boxShadow: themeName === t.id ? '0 4px 16px rgba(83,74,183,0.20)' : undefined,
                  cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
                  transition: 'all 0.15s',
                }}>
                  <div style={{ width: '100%', height: 26, borderRadius: 8, overflow: 'hidden', display: 'flex', border: '1px solid rgba(255,255,255,0.08)' }}>
                    <div style={{ flex: 1, background: t.bg }} />
                    <div style={{ flex: '0 0 10px', background: t.accent }} />
                  </div>
                  <span style={{ fontSize: 10, fontFamily: 'Syne, sans-serif', fontWeight: themeName === t.id ? 700 : 400, color: themeName === t.id ? theme.accent : theme.muted }}>{t.icon} {t.label}</span>
                </button>
              ))}
            </div>
          </div>
        </Section>

        {/* ── LANGUAGE ── */}
        <Section title="Language / Мова">
          <div style={{ padding: '12px 16px', display: 'flex', gap: 10, position: 'relative', zIndex: 2 }}>
            {(['en', 'uk'] as const).map(l => (
              <button key={l} onClick={() => setLang(l)} style={{
                flex: 1, padding: '11px 8px', borderRadius: 12,
                background: lang === l ? 'rgba(83,74,183,0.22)' : 'rgba(255,255,255,0.04)',
                backdropFilter: 'blur(12px)',
                border: `1px solid ${lang === l ? 'rgba(127,119,221,0.45)' : 'rgba(255,255,255,0.08)'}`,
                color: lang === l ? theme.text : theme.muted,
                fontFamily: 'Syne, sans-serif', fontSize: 13, fontWeight: lang === l ? 700 : 400,
                cursor: 'pointer', transition: 'all 0.15s',
                boxShadow: lang === l ? '0 4px 14px rgba(83,74,183,0.20)' : undefined,
              }}>
                {l === 'en' ? '🇬🇧 English' : '🇺🇦 Українська'}
              </button>
            ))}
          </div>
        </Section>

        {/* ── DESKTOP AI ── */}
        <Section title="Desktop AI">
          <Row icon="🤖" label="AI Feedback" sub={wsEnabled ? 'Enabled — receives real-time tips' : 'Disabled'}
            action={<Toggle value={wsEnabled} onChange={() => setWsEnabled(!wsEnabled)} />}
          />
          <div style={{ padding: '12px 16px', borderTop: '1px solid rgba(255,255,255,0.06)', position: 'relative', zIndex: 2 }}>
            <p style={{ color: 'rgba(238,237,254,0.40)', fontSize: 11, marginBottom: 8, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Desktop IP address</p>
            <div style={{ display: 'flex', gap: 8 }}>
              <input type="text" value={hostInput} onChange={e => setHostInput(e.target.value)} placeholder="192.168.1.100"
                style={{ flex: 1, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 10, padding: '10px 12px', color: '#EEEDFE', fontSize: 14, fontFamily: 'JetBrains Mono, monospace', outline: 'none' }} />
              <button onClick={() => setDesktopHost(hostInput)} style={{
                padding: '10px 16px', borderRadius: 10,
                background: 'rgba(83,74,183,0.28)', border: '1px solid rgba(127,119,221,0.40)',
                color: '#EEEDFE', fontSize: 13, fontFamily: 'Syne, sans-serif', fontWeight: 700, cursor: 'pointer',
              }}>Save</button>
            </div>
            <button onClick={() => setScanning(s => !s)} style={{
              width: '100%', marginTop: 8, padding: '11px', borderRadius: 12,
              background: scanning ? 'rgba(127,119,221,0.18)' : 'rgba(255,255,255,0.04)',
              border: `1px solid ${scanning ? 'rgba(127,119,221,0.40)' : 'rgba(255,255,255,0.08)'}`,
              color: theme.accent, fontSize: 13, fontFamily: 'Syne, sans-serif', fontWeight: 600, cursor: 'pointer',
            }}>
              {scanning ? '✕ Cancel' : '📱 Scan QR from desktop'}
            </button>
            {scanning && (
              <div style={{ marginTop: 8, borderRadius: 14, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.08)' }}>
                <QRScanner onResult={handleQRResult} />
              </div>
            )}
          </div>
        </Section>

        {/* ── ABOUT ── */}
        <Section title="About">
          <Row icon="🎵" label="Velora" sub="Version 1.0.0 · YIN Pitch Detection · WebSocket AI" border={false}
            action={<span style={{ color: theme.dim, fontSize: 11 }}>v1.0</span>}
          />
        </Section>

      </div>
    </div>
  );
}
